import React from 'react';
import { ChevronRightIcon, CheckCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

const ModelsByIndustryPage: React.FC = () => {
  const industries = [
    {
      name: 'Legal Industry',
      subtitle: 'Law Firms & In-House Counsel',
      description: 'Legal professionals use GenAI to draft and review contracts, summarize case law, perform legal research, and answer regulatory questions.',
      useCases: [
        'Contract drafting and review',
        'Case law summarization',
        'Legal research automation',
        'Regulatory compliance Q&A'
      ],
      models: [
        {
          name: 'SaulLM-7B',
          description: 'A 7-billion-parameter model explicitly designed for legal text comprehension and generation',
          details: 'Trained on 30B tokens of legal cases and legislation, achieved state-of-the-art proficiency in understanding legal documents. Open-source (CC BY-SA license).',
          readiness: 'High',
          license: 'CC BY-SA'
        },
        {
          name: 'Law LLM (AdaptLLM)',
          description: 'A 7B model fine-tuned via "reading comprehension" approach on legal corpora',
          details: 'Competes with much larger models on domain-specific tasks. Lightweight open models can be production-ready for legal Q&A and document analysis.',
          readiness: 'High',
          license: 'Open Source'
        },
        {
          name: 'Legal-BERT Family',
          description: 'Set of smaller transformer models pre-trained on diverse legal texts',
          details: 'Encoder models excel at legal NLP tasks like classification or extraction. Can be integrated with LLMs for better accuracy.',
          readiness: 'Medium',
          license: 'Open Source'
        }
      ],
      readiness: 'Many legal AI models are emerging from research but quickly maturing. Law firms can experiment now with open availability.'
    },
    {
      name: 'Healthcare & Medical',
      subtitle: 'Clinical Decision Support & Research',
      description: 'Doctors, hospitals, and researchers leverage GenAI for clinical decision support, summarizing medical records, and analyzing biomedical literature.',
      useCases: [
        'Clinical decision support',
        'Medical record summarization',
        'Patient query responses',
        'Biomedical literature analysis'
      ],
      models: [
        {
          name: 'MEDITRON-70B',
          description: '70-billion parameter model tuned for clinical knowledge',
          details: 'High-parameter model for expert-level performance in medical Q&A. Released under permissive terms.',
          readiness: 'High',
          license: 'Open Source'
        },
        {
          name: 'ChatDoctor',
          description: '13B model trained for doctor-patient dialogues',
          details: 'Fine-tuned for medical Q&A. Performs well on medical exam benchmarks and can be deployed on modest hardware.',
          readiness: 'High',
          license: 'Open Source'
        },
        {
          name: 'BioGPT',
          description: '350M parameter model for biomedical literature',
          details: 'Smaller model that can generate biomedical literature summaries. Useful for research support.',
          readiness: 'Medium',
          license: 'Open Source'
        }
      ],
      readiness: 'Many medical LLMs are open-source and community-tested. Several show performance approaching closed models on medical questions.'
    },
    {
      name: 'Financial Services',
      subtitle: 'Banking & Finance',
      description: 'Banks, investment firms, and insurers apply GenAI to analyze financial reports, market news, and regulations.',
      useCases: [
        'Financial report analysis',
        'Market news processing',
        'Regulatory compliance',
        'Client advisory assistance'
      ],
      models: [
        {
          name: 'FinGPT',
          description: 'Open-source financial LLM framework',
          details: 'Data-centric approach with tools to curate financial data. Enables building custom financial AI without relying on single providers.',
          readiness: 'High',
          license: 'Open Source'
        },
        {
          name: 'Finance-LLM (7B)',
          description: '7B parameter model continued-training on financial texts',
          details: 'Can rival larger models on industry benchmarks. Matches some BloombergGPT capabilities despite smaller size.',
          readiness: 'High',
          license: 'Open Source'
        },
        {
          name: 'FinBERT',
          description: 'BERT model tuned for financial sentiment analysis',
          details: 'While not generative, excels at analyzing sentiment in financial news and social media.',
          readiness: 'High',
          license: 'Open Source'
        }
      ],
      readiness: 'Finance sector moving quickly on GenAI. Open models can achieve ~70-80% of GPT-4 performance on finance tasks.'
    },
    {
      name: 'Manufacturing & Engineering',
      subtitle: 'Industrial Sector',
      description: 'Manufacturers and engineering companies use GenAI to capture and disseminate complex technical knowledge.',
      useCases: [
        'Engineering assistant chatbots',
        'Sensor log analysis',
        'Maintenance optimization',
        'Product design assistance'
      ],
      models: [
        {
          name: 'Industrial LLM (SymphonyAI)',
          description: 'World\'s first industrial-focused large language model',
          details: 'Trained on massive datasets of sensor readings, equipment maintenance logs, and failure reports.',
          readiness: 'Medium',
          license: 'Proprietary'
        },
        {
          name: 'Galactica-6.7B',
          description: 'Scientific LLM for technical literature',
          details: 'Trained on 48 million papers, textbooks, and reference materials. Available open-source.',
          readiness: 'Medium',
          license: 'Open Source'
        },
        {
          name: 'Custom Fine-tunes',
          description: 'Fine-tuned general models on internal data',
          details: 'Many firms fine-tune LLaMA-2 or Falcon on internal technical documentation and manuals.',
          readiness: 'High',
          license: 'Custom'
        }
      ],
      readiness: 'Industrial GenAI emerging but early trials promising. Companies starting with decision support rather than autonomous actions.'
    },
    {
      name: 'Consulting & Professional Services',
      subtitle: 'Knowledge Management & Client Services',
      description: 'Consulting firms leverage GenAI to access collective knowledge bases and draft client deliverables.',
      useCases: [
        'Internal research gathering',
        'Client deliverable drafting',
        'Proposal creation',
        'Client data analysis'
      ],
      models: [
        {
          name: 'Internal Knowledge Chatbots',
          description: 'Proprietary GPT-powered assistants',
          details: 'Like McKinsey\'s "Lilli" trained on 100 years of firm documents. Used by 70% of employees.',
          readiness: 'High',
          license: 'Proprietary'
        },
        {
          name: 'Custom Fine-tunes',
          description: 'Fine-tuned open models on proprietary data',
          details: 'Firms fine-tune 13B models on past project reports and style guides.',
          readiness: 'High',
          license: 'Custom'
        },
        {
          name: 'Agentic Tools',
          description: 'Multi-step LLM chains',
          details: 'Using frameworks like LangChain to build complex workflows internally.',
          readiness: 'Medium',
          license: 'Open Source'
        }
      ],
      readiness: 'Already in production. Internal AI assistants considered standard tools with clear ROI in time savings.'
    },
    {
      name: 'Customer Service & Retail',
      subtitle: 'Customer Engagement & Content Generation',
      description: 'Businesses use AI chatbots and content generators for customer-facing operations with brand consistency.',
      useCases: [
        'Customer support automation',
        'Product recommendations',
        'Content generation',
        'Marketing automation'
      ],
      models: [
        {
          name: 'Dolly 2.0',
          description: '12B open instruction-following model',
          details: 'First truly commercial-ready ChatGPT-like model. Training data and weights open-sourced for commercial use.',
          readiness: 'High',
          license: 'Open Source'
        },
        {
          name: 'Stable Diffusion',
          description: 'Open-source text-to-image model',
          details: 'Can be fine-tuned on product photos for brand-specific image generation. Secure on-premise deployment.',
          readiness: 'High',
          license: 'Open Source'
        },
        {
          name: 'LLaVA',
          description: 'Language-and-Vision Assistant',
          details: 'Multimodal model combining text and image understanding for advanced customer interactions.',
          readiness: 'Medium',
          license: 'Open Source'
        }
      ],
      readiness: 'Moving from pilot to production. Many companies upgrading FAQ bots with LLMs for natural dialogue.'
    },
    {
      name: 'Media & Entertainment',
      subtitle: 'Content Creation & Creative Industries',
      description: 'Media companies use GenAI to generate written content, imagery, and creative assets while preserving IP.',
      useCases: [
        'Content drafting',
        'Image and video generation',
        'Script writing',
        'Concept art creation'
      ],
      models: [
        {
          name: 'LLaMA-2',
          description: 'Meta\'s 7B–70B LLM for creative writing',
          details: 'Open-source or permissively licensed. Can be fine-tuned on specific styles or story universes.',
          readiness: 'High',
          license: 'Open Source'
        },
        {
          name: 'Stable Diffusion XL',
          description: 'High-resolution image generation',
          details: 'Can be fine-tuned with few images to learn new styles. Used extensively in creative industries.',
          readiness: 'High',
          license: 'Open Source'
        },
        {
          name: 'Audio Models',
          description: 'Speech and music generation',
          details: 'Models like Tacotron for speech synthesis. Used for prototyping dialogue and audio content.',
          readiness: 'Medium',
          license: 'Open Source'
        }
      ],
      readiness: 'Quickly embraced for efficiency. AI-generated visuals already in production. Models ready for creative support tasks.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-800 to-secondary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Private GenAI Models by Industry
            </h1>
            <p className="text-xl md:text-2xl text-primary-100 max-w-4xl mx-auto leading-relaxed">
              Industry-Specific Generative AI Models for Private Use
            </p>
            <p className="text-lg text-primary-200 mt-6 max-w-3xl mx-auto">
              Unlike public AI services, private GenAI solutions let organizations self-host or fine-tune AI models on their own data, 
              preserving confidentiality and compliance. Explore key industries benefiting from private GenAI.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="flex items-start space-x-4">
            <InformationCircleIcon className="w-8 h-8 text-primary-600 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Why Private GenAI?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Generative AI is transforming many industries by automating content creation, analysis, and communication. 
                All the models listed have open weights or enterprise-friendly licenses, meaning businesses can adapt and 
                deploy them in-house without exposing sensitive data to third-party services.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Many examples show that even relatively small models (7B–13B), when specialized, can outperform giant 
                general models in niche tasks. The focus can now shift to implementation details: data preparation, 
                validation, and the human-AI workflow.
              </p>
            </div>
          </div>
        </div>

        {/* Industries Grid */}
        <div className="space-y-12">
          {industries.map((industry, index) => (
            <div key={industry.name} className="bg-white rounded-xl shadow-lg overflow-hidden">
              {/* Industry Header */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-8 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">{industry.name}</h2>
                    <p className="text-xl text-gray-600 mb-4">{industry.subtitle}</p>
                    <p className="text-gray-700 leading-relaxed max-w-4xl">
                      {industry.description}
                    </p>
                  </div>
                  <div className="hidden lg:flex items-center space-x-2 text-sm text-gray-500">
                    <span>Industry #{index + 1}</span>
                    <ChevronRightIcon className="w-4 h-4" />
                  </div>
                </div>
              </div>

              <div className="p-8">
                {/* Use Cases */}
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Key Use Cases</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {industry.useCases.map((useCase) => (
                      <div key={useCase} className="flex items-center space-x-3">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">{useCase}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Models */}
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Domain Models & Readiness</h3>
                  <div className="space-y-6">
                    {industry.models.map((model) => (
                      <div key={model.name} className="border border-gray-200 rounded-lg p-6">
                        <div className="flex items-start justify-between mb-3">
                          <h4 className="text-lg font-semibold text-gray-900">{model.name}</h4>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              model.readiness === 'High' 
                                ? 'bg-green-100 text-green-800'
                                : model.readiness === 'Medium'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {model.readiness} Readiness
                            </span>
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {model.license}
                            </span>
                          </div>
                        </div>
                        <p className="text-gray-700 font-medium mb-2">{model.description}</p>
                        <p className="text-gray-600 text-sm leading-relaxed">{model.details}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Readiness Assessment */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Industry Readiness</h3>
                  <p className="text-gray-700 leading-relaxed">{industry.readiness}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Conclusion */}
        <div className="bg-white rounded-xl shadow-lg p-8 mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Conclusion</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Across these industries – from highly regulated fields like law and medicine to creative sectors – 
            private, domain-adapted generative AI is unlocking new efficiencies and capabilities.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            Crucially, the emergence of powerful open-source models has made it possible for organizations to 
            adopt GenAI on their own terms. They can fine-tune models on proprietary data, ensuring the AI 
            understands industry-specific context, while keeping data secure.
          </p>
          <p className="text-gray-700 leading-relaxed">
            For businesses considering this journey, the landscape of options is growing rapidly. Model readiness 
            is no longer a major barrier – in 2025 we have a menu of mature, community-validated LLMs and 
            generative models for almost every industry.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ModelsByIndustryPage;